import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/src/contexts/AuthContext'
import { KycProvider } from '@/src/contexts/KycContext'
import { DemoModeProvider } from '@/src/contexts/DemoModeContext'
import { ProtectedRoute } from '@/src/components/auth/ProtectedRoute'
import { KycGuard, KycVerifiedGuard } from '@/src/components/auth/KycGuard'
import UnauthorizedPage from '@/src/components/auth/UnauthorizedPage'

// Pages
import LoginPage from '@/app/auth/login/page'
import ForgotPasswordPage from '@/app/auth/forgot-password/page'
import RegisterPage from '@/app/auth/register/page'
import RegisterStep2 from '@/app/auth/register/step-2/page'
import RegisterStep3 from '@/app/auth/register/step-3/page'
import VerifyPage from '@/app/auth/verify/page'
import VerifyIndividualPage from '@/app/auth/verify/individual/page'
import VerifyCompanyPage from '@/app/auth/verify/company/page'
import DashboardPage from '@/app/dashboard/page'
import ComingSoonPage from '@/app/dashboard/coming-soon/page'
import TeamPage from '@/app/dashboard/team/page'
import TeamInvitePage from '@/app/dashboard/team/invite/page'
import AccountsPage from '@/app/dashboard/accounts/page'
import AdminManagePage from '@/app/dashboard/admin-manage/page'
import BranchPage from '@/app/dashboard/branch/page'
import EditStorePage from '@/app/dashboard/edit-store/page'
import ConnectsPage from '@/app/dashboard/connects/page'
import SlipsManagePage from '@/app/dashboard/slips-manage/page'
import { DashboardLayout } from '@/components/dashboard/dashboard-layout'

// Public pages
import AgreementPage from '@/app/page/agreement/page'
import PolicyPage from '@/app/page/policy/page'
import AcceptInvitationPage from '@/app/accept-invitation/[token]/page'

// History pages
import HistoryAllPage from '@/app/history/all/page'
import HistoryApiPage from '@/app/history/api/page'
import HistoryChecksPage from '@/app/history/checks/page'

// Settings pages
import SettingsAccountsPage from '@/app/settings/accounts/page'
import SettingsPackagesPage from '@/app/settings/packages/page'
import SettingsSlipChecksPage from '@/app/settings/slip-checks/page'
import PurchasePackagePage from '@/app/settings/packages/purchase-package'

// User pages
import UserAddCreditPage from '@/app/user/add-credit/page'
import PaymentReturnPage from '@/app/payment/return/page'
import ReservePayPaymentPage from '@/app/payment/reservepay/page'

// API Connect pages
import ApiConnectAuthPage from '@/app/api-connect/auth/page'
import ApiConnectEndpointPage from '@/app/api-connect/endpoint/page'
import ApiConnectResponsePage from '@/app/api-connect/response/page'
import ApiConnectGuidePage from '@/app/api-connect/guide/page'

// Admin components
import AdminLogin from '@/src/components/admin/AdminLogin'
import AdminLayout from '@/src/components/admin/AdminLayout'
import AdminDashboard from '@/src/components/admin/AdminDashboard'
import AdminKycDashboard from '@/src/components/admin/AdminKycDashboard'
import AdminKycDetail from '@/src/components/admin/AdminKycDetail'
import AdminUsers from '@/src/components/admin/AdminUsers'
import AdminPaymentSlips from '@/src/components/admin/AdminPaymentSlips'
import AdminReservePayTransactions from '@/src/components/admin/AdminReservePayTransactions'
import AdminReports from '@/src/components/admin/AdminReports'
import AdminReportRevenue from '@/src/components/admin/reports/AdminReportRevenue'
import AdminReportCustomers from '@/src/components/admin/reports/AdminReportCustomers'
import AdminReportPerformance from '@/src/components/admin/reports/AdminReportPerformance'
import AdminReportVerifications from '@/src/components/admin/reports/AdminReportVerifications'
import AdminReportCredits from '@/src/components/admin/reports/AdminReportCredits'
import AdminReportBankUsageCredits from '@/src/components/admin/reports/AdminReportBankUsageCredits'
import AdminReportKyc from '@/src/components/admin/reports/AdminReportKyc'
import AdminSystemSettings from '@/src/components/admin/AdminSystemSettings'
import AdminSecuritySettings from '@/src/components/admin/AdminSecuritySettings'
import AdminOperationsDashboard from '@/src/components/admin/AdminOperationsDashboard'
import AdminTechnicalDashboard from '@/src/components/admin/AdminTechnicalDashboard'
import AdminBankHealth from '@/src/components/admin/AdminBankHealth'
import AdminMarketingDashboard from '@/src/components/admin/AdminMarketingDashboard'
import PackageList from '@/src/components/admin/packages/PackageList'
import PackageForm from '@/src/components/admin/packages/PackageForm'
import PackageDetail from '@/src/components/admin/packages/PackageDetail'
import AdminApiActivity from '@/src/components/admin/AdminApiActivity'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <DemoModeProvider>
      <AuthProvider>
      <KycProvider>
        <div className="font-noto-sans-thai">
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              {/* Root redirects to dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              
              {/* Auth routes - Public */}
              <Route path="/auth/login" element={<LoginPage />} />
              <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/register" element={<RegisterPage />} />
              <Route path="/auth/register/step-2" element={<RegisterStep2 />} />
              <Route path="/auth/register/step-3" element={<RegisterStep3 />} />
              
              {/* Admin Login - Public */}
              <Route path="/admin/login" element={<AdminLogin />} />
              
              <Route path="admin" element={<AdminLayout />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="operations" element={<AdminOperationsDashboard />} />
                <Route path="technical" element={<AdminTechnicalDashboard />} />
                <Route path="bank-health" element={<AdminBankHealth />} />
                <Route path="marketing" element={<AdminMarketingDashboard />} />
                <Route path="kyc" element={<AdminKycDashboard />} />
                <Route path="kyc/:id" element={<AdminKycDetail />} />
                <Route path="packages" element={<PackageList />} />
                <Route path="packages/new" element={<PackageForm />} />
                <Route path="packages/:id" element={<PackageDetail />} />
                <Route path="packages/:id/edit" element={<PackageForm />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="payment-slips" element={<AdminPaymentSlips />} />
                <Route path="reservepay-transactions" element={<AdminReservePayTransactions />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="reports/revenue" element={<AdminReportRevenue />} />
                <Route path="reports/customers" element={<AdminReportCustomers />} />
                <Route path="reports/performance" element={<AdminReportPerformance />} />
                <Route path="reports/verifications" element={<AdminReportVerifications />} />
                <Route path="reports/credits" element={<AdminReportCredits />} />
                <Route path="reports/bank-usage-credits" element={<AdminReportBankUsageCredits />} />
                <Route path="reports/kyc" element={<AdminReportKyc />} />
                <Route path="api-activity" element={<AdminApiActivity />} />
                <Route path="settings/system" element={<AdminSystemSettings />} />
                <Route path="settings/security" element={<AdminSecuritySettings />} />
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
              </Route>
              
              {/* KYC Verification routes - Public (accessible after registration) */}
              <Route path="/auth/verify" element={<VerifyPage />} />
              <Route path="/auth/verify/individual" element={<VerifyIndividualPage />} />
              <Route path="/auth/verify/company" element={<VerifyCompanyPage />} />
              
              {/* Public pages */}
              <Route path="/page/agreement" element={<AgreementPage />} />
              <Route path="/page/policy" element={<PolicyPage />} />
              <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />
              
              {/* Unauthorized page */}
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              
              <Route element={<DashboardLayout />}>
                {/* Dashboard routes - Protected + KYC Required */}
                <Route path="/dashboard" element={
                  <ProtectedRoute permissions={["dashboard:view"]}>
                    <KycGuard>
                      <DashboardPage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/coming-soon" element={
                  <ProtectedRoute permissions={["dashboard:view"]}>
                    <KycGuard>
                      <ComingSoonPage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/accounts" element={
                  <ProtectedRoute permissions={["accounts:view"]}>
                    <KycGuard>
                      <AccountsPage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/team" element={
                  <ProtectedRoute permissions={["team:view"]} roles={["owner", "admin"]}>
                    <KycGuard>
                      <TeamPage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/team/invite" element={
                  <ProtectedRoute permissions={["team:invite"]}>
                    <KycGuard>
                      <TeamInvitePage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/admin-manage" element={
                  <ProtectedRoute permissions={["slip:verify"]} roles={["customer_manager", "customer_admin"]}>
                    <KycGuard>
                      <AdminManagePage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/branch" element={
                  <ProtectedRoute permissions={["branch:view"]}>
                    <KycGuard>
                      <BranchPage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/edit-store" element={
                  <ProtectedRoute permissions={["dashboard:view"]}>
                    <KycGuard>
                      <EditStorePage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/connects" element={
                  <ProtectedRoute permissions={["dashboard:view"]}>
                    <KycVerifiedGuard>
                      <ConnectsPage />
                    </KycVerifiedGuard>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard/slips-manage" element={
                  <ProtectedRoute permissions={["slip:verify"]}>
                    <KycVerifiedGuard>
                      <SlipsManagePage />
                    </KycVerifiedGuard>
                  </ProtectedRoute>
                } />
                
                {/* History routes - Protected + KYC Required */}
                <Route path="/history/all" element={
                  <ProtectedRoute permissions={["billing:view"]}>
                    <KycGuard>
                      <HistoryAllPage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                <Route path="/history/api" element={
                  <ProtectedRoute permissions={["api:view"]}>
                    <KycGuard>
                      <HistoryApiPage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                <Route path="/history/checks" element={
                  <ProtectedRoute permissions={["reports:view"]}>
                    <KycGuard>
                      <HistoryChecksPage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                
                {/* Settings routes - Protected + KYC Required */}
                <Route path="/settings/accounts" element={
                  <ProtectedRoute permissions={["slip:view"]}>
                    <KycGuard>
                      <SettingsAccountsPage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                <Route path="/settings/packages" element={
                  <ProtectedRoute permissions={["billing:view"]}>
                    <KycVerifiedGuard>
                      <SettingsPackagesPage />
                    </KycVerifiedGuard>
                  </ProtectedRoute>
                } />
                <Route path="/settings/packages/purchase/:packageId" element={
                  <ProtectedRoute permissions={["billing:view"]}>
                    <KycVerifiedGuard>
                      <PurchasePackagePage />
                    </KycVerifiedGuard>
                  </ProtectedRoute>
                } />
                <Route path="/settings/slip-checks" element={
                  <ProtectedRoute permissions={["slip:view"]}>
                    <KycGuard>
                      <SettingsSlipChecksPage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                
                {/* User routes - Protected + KYC Required */}
                <Route path="/user/add-credit" element={
                  <ProtectedRoute permissions={["dashboard:view"]}>
                    <KycGuard>
                      <UserAddCreditPage />
                    </KycGuard>
                  </ProtectedRoute>
                } />
                
                {/* Payment return (ReservePay redirect) */}
                <Route path="/payment/return" element={
                  <ProtectedRoute permissions={["dashboard:view"]}>
                    <PaymentReturnPage />
                  </ProtectedRoute>
                } />

                {/* ReservePay full-page QR payment flow */}
                <Route path="/payment/reservepay" element={
                  <ProtectedRoute permissions={["dashboard:view"]}>
                    <ReservePayPaymentPage />
                  </ProtectedRoute>
                } />

                {/* API Connect routes - Protected + KYC Required */}
                <Route path="/api-connect/guide" element={
                  <ProtectedRoute permissions={["api:use"]}>
                    <KycVerifiedGuard>
                      <ApiConnectGuidePage />
                    </KycVerifiedGuard>
                  </ProtectedRoute>
                } />
                <Route path="/api-connect/auth" element={
                  <ProtectedRoute permissions={["api:use"]}>
                    <KycVerifiedGuard>
                      <ApiConnectAuthPage />
                    </KycVerifiedGuard>
                  </ProtectedRoute>
                } />
                <Route path="/api-connect/endpoint" element={
                  <ProtectedRoute permissions={["api:use"]}>
                    <KycVerifiedGuard>
                      <ApiConnectEndpointPage />
                    </KycVerifiedGuard>
                  </ProtectedRoute>
                } />
                <Route path="/api-connect/response" element={
                  <ProtectedRoute permissions={["api:use"]}>
                    <KycVerifiedGuard>
                      <ApiConnectResponsePage />
                    </KycVerifiedGuard>
                  </ProtectedRoute>
                } />
              </Route>
              
              {/* Catch all - redirect to dashboard */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </div>
      </KycProvider>
    </AuthProvider>
      </DemoModeProvider>
    </QueryClientProvider>
  )
}

export default App
